import { useMemo, useState } from 'react';
import {
  apiErrText, createWorkInstruction, updateWorkInstructionDraft, issueWorkInstruction,
  DAYWORK_CATEGORY_LABEL, WI_ORIGIN_LABEL, WI_WARNING_COPY,
  type ContractForm, type InstructionDetail, type InstructionDraftInput, type InstructionLineInput,
  type WorkInstructionKind, type WorkItem,
} from '../lib/api';
import { toast } from './ui/Toast';

/**
 * 新建 / 编辑工程指令 — the form that turns a site decision into a written order (合同 9.1).
 *
 * Three rules the layout enforces before the server has to:
 *   1. 发生日期 defaults to BEIJING today and cannot be in the future. Every clock in Layer 2 (the 7-day
 *      quote window, the tier-④ 造价信息 month) hangs off this date, so a wrong one is not cosmetic.
 *   2. NO RATES. The instruction says what was built, never what it costs — 预算 prices it later, and the
 *      server answers 400 `rates_not_allowed_here` to a line that carries one. 预估金额 is a separate
 *      容错额度 routing field and is hidden from 项目经理 entirely.
 *   3. 来源=甲方提出 needs 商务 acknowledgement first (9.1.2). Said here as a hint; refused there as a 409.
 *
 * Warnings come back from the create/update call and render as yellow banners — they never block 下发.
 */

/** Beijing (fixed UTC+8) calendar date — the same clock `lib/beijingClock.ts` keeps on the server. */
export function beijingToday(now: Date = new Date()): string {
  return new Date(now.getTime() + 8 * 3600 * 1000).toISOString().slice(0, 10);
}

const ORIGINS = ['site', 'client', 'design', 'labor', 'wtl'];
const CATEGORY_KEYS = Object.keys(DAYWORK_CATEGORY_LABEL);

const blankLine = (): InstructionLineInput => ({ item_label: '', unit: '', qty: null, work_item_id: null, spec_md: '', measurement_calc_md: '' });

export default function InstructionEditor({ scId, contractForm, workItems, existing, initialKind, isPm, onClose, onSaved }: {
  scId: string;
  contractForm: ContractForm | null;
  workItems: WorkItem[];
  /** An existing DRAFT being edited; omit to create. */
  existing?: InstructionDetail;
  /** Which of the two 新建 buttons was pressed (变更 vs 零星); ignored when editing a draft. */
  initialKind?: WorkInstructionKind;
  /** 项目经理: the 预估金额 field is not theirs — the PM never types money. */
  isPm: boolean;
  onClose: () => void;
  onSaved: (d: InstructionDetail) => void;
}) {
  const wi = existing?.instruction;
  const [kind, setKind] = useState<WorkInstructionKind>(wi?.kind ?? initialKind ?? 'variation');
  const [title, setTitle] = useState(wi?.title ?? '');
  const [origin, setOrigin] = useState(wi?.origin ?? 'site');
  const [occurredOn, setOccurredOn] = useState(wi?.occurred_on ?? beijingToday());
  const [reason, setReason] = useState(wi?.reason_md ?? '');
  const [scope, setScope] = useState(wi?.scope_md ?? '');
  const [location, setLocation] = useState(wi?.location_md ?? '');
  const [stage, setStage] = useState(wi?.stage_md ?? '');
  const [drawingRef, setDrawingRef] = useState(wi?.drawing_ref ?? '');
  const [category, setCategory] = useState(wi?.daywork_category ?? '');
  const [scheduleDays, setScheduleDays] = useState<string>(wi?.schedule_impact_days != null ? String(wi.schedule_impact_days) : '');
  const [estimate, setEstimate] = useState<string>(wi?.estimate_rmb != null ? String(wi.estimate_rmb) : '');
  const [reworkRequired, setReworkRequired] = useState(!!wi?.rework_required);
  const [lines, setLines] = useState<InstructionLineInput[]>(
    existing?.lines.length
      ? existing.lines.map((l) => ({
        item_label: l.item_label, unit: l.unit ?? '', qty: l.qty ?? null,
        work_item_id: l.work_item_id ?? null, spec_md: l.spec_md ?? '', measurement_calc_md: l.measurement_calc_md ?? '',
      }))
      : [blankLine()],
  );
  const [warnings, setWarnings] = useState<string[]>(existing?.warnings ?? []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(wi?.id ?? null);

  const today = beijingToday();
  const futureDate = occurredOn > today;
  const canSave = !!title.trim() && !!occurredOn && !futureDate
    && lines.some((l) => l.item_label.trim())
    && (kind !== 'daywork' || !!category);

  function body(): InstructionDraftInput {
    return {
      kind, title: title.trim(), origin: origin || null, occurred_on: occurredOn,
      reason_md: reason.trim() || null, scope_md: scope.trim() || null,
      location_md: location.trim() || null, stage_md: stage.trim() || null,
      drawing_ref: drawingRef.trim() || null,
      daywork_category: kind === 'daywork' ? category || null : null,
      schedule_impact_days: scheduleDays === '' ? null : Number(scheduleDays),
      // 项目经理 never sends an estimate — the field is not rendered for them, and nothing is sent either.
      ...(isPm ? {} : { estimate_rmb: estimate === '' ? null : Number(estimate) }),
      rework_required: reworkRequired,
      lines: lines.filter((l) => l.item_label.trim()).map((l) => ({
        item_label: l.item_label.trim(),
        unit: l.unit || null,
        qty: l.qty == null || Number.isNaN(l.qty) ? null : Number(l.qty),
        work_item_id: l.work_item_id || null,
        spec_md: l.spec_md?.trim() || null,
        measurement_calc_md: l.measurement_calc_md?.trim() || null,
      })),
    };
  }

  async function saveDraft(): Promise<InstructionDetail | null> {
    setBusy(true); setErr(null);
    try {
      const d = draftId ? await updateWorkInstructionDraft(draftId, body()) : await createWorkInstruction(scId, body());
      setDraftId(d.instruction.id);
      setWarnings(d.warnings ?? []);
      onSaved(d);
      return d;
    } catch (e) { setErr(apiErrText(e)); return null; }
    finally { setBusy(false); }
  }

  async function saveAndIssue() {
    const d = await saveDraft();
    if (!d) return;
    setBusy(true); setErr(null);
    try {
      const issued = await issueWorkInstruction(d.instruction.id);
      toast('已下发，等待三方签字 / issued — awaiting the three signatures', { kind: 'success' });
      onSaved(issued);
      onClose();
    } catch (e) { setErr(apiErrText(e)); }
    finally { setBusy(false); }
  }

  function setLine(i: number, patch: Partial<InstructionLineInput>) {
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  }

  return (
    <div className="card drawer" style={{ borderColor: 'var(--accent)', marginBottom: 12 }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <strong>{draftId ? '编辑工程指令 / Edit instruction' : '新建工程指令 / New work instruction'}</strong>
        {draftId && <span className="pill pill-muted small">草稿 / draft</span>}
        <span style={{ flex: 1 }} />
        <button className="small" style={{ minHeight: 44 }} onClick={onClose}>× 关闭 / Close</button>
      </div>

      {/* create/update warnings — advisory, never blockers (§2.2) */}
      {warnings.map((w) => (
        <div key={w} className="small" style={{ background: 'var(--warn-soft)', border: '1px solid var(--warn)', padding: '8px 10px', borderRadius: 6, marginBottom: 8 }}>
          ⚠ {WI_WARNING_COPY[w] ?? w}
        </div>
      ))}

      <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>类型 / kind</label>
          <select value={kind} onChange={(e) => setKind(e.target.value as WorkInstructionKind)} style={{ minHeight: 44 }}>
            <option value="variation">变更 / Variation</option>
            <option value="daywork">零星（计日工）/ Daywork</option>
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>来源 / origin</label>
          <select value={origin ?? ''} onChange={(e) => setOrigin(e.target.value)} style={{ minHeight: 44 }}>
            {ORIGINS.map((o) => <option key={o} value={o}>{WI_ORIGIN_LABEL[o]}</option>)}
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>发生日期 / occurred on（北京时间 Beijing）</label>
          <input type="date" value={occurredOn} max={today} onChange={(e) => setOccurredOn(e.target.value)} style={{ minHeight: 44 }} />
        </div>
        <div className="field" style={{ marginBottom: 0, flex: '1 1 220px' }}>
          <label>标题 / title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} style={{ minHeight: 44, width: '100%' }} placeholder="例：三层东侧墙面基层做法变更" />
        </div>
      </div>

      {/* 9.1.2 — a client-raised variation needs 商务 to acknowledge before it goes out as our order */}
      {origin === 'client' && (
        <div className="small" style={{ color: 'var(--accent)', marginTop: 6 }}>
          ℹ 甲方提出需先经商务确认（合同 9.1.2）— 未确认时「下发」会被拒绝。/ a client-raised instruction needs 商务 acknowledgement before it can be issued.
        </div>
      )}
      {futureDate && (
        <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>发生日期不能是将来的日期 / the occurrence date cannot be in the future.</div>
      )}

      <div className="row-wrap" style={{ gap: 8, marginTop: 8 }}>
        <div className="field" style={{ flex: '1 1 240px' }}><label>事由 / reason</label><textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} /></div>
        <div className="field" style={{ flex: '1 1 240px' }}><label>内容 / scope of work</label><textarea value={scope} onChange={(e) => setScope(e.target.value)} rows={2} /></div>
      </div>
      <div className="row-wrap" style={{ gap: 8 }}>
        <div className="field" style={{ flex: '1 1 200px' }}><label>部位 / location</label><input value={location} onChange={(e) => setLocation(e.target.value)} style={{ minHeight: 44 }} /></div>
        <div className="field" style={{ flex: '1 1 200px' }}><label>阶段 / stage</label><input value={stage} onChange={(e) => setStage(e.target.value)} style={{ minHeight: 44 }} /></div>
        <div className="field" style={{ marginBottom: 8 }}><label>工期影响（天）/ schedule impact (days)</label><input type="number" value={scheduleDays} onChange={(e) => setScheduleDays(e.target.value)} style={{ minHeight: 44, width: 120 }} /></div>
      </div>

      {/* 图纸变更号 — only meaningful on a 固定总价 contract, where the drawing revision is how 甲方 checks it */}
      {contractForm === 'lump_sum' && (
        <div className="field">
          <label>图纸变更号 / drawing revision no.（固定总价合同）</label>
          <input value={drawingRef} onChange={(e) => setDrawingRef(e.target.value)} style={{ minHeight: 44 }} />
        </div>
      )}

      {kind === 'daywork' && (
        <div className="field">
          <label>零星类别 / daywork category（合同 9.2.1–9.2.10，必选）</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ minHeight: 44, width: '100%' }}>
            <option value="">请选择 / pick one…</option>
            {CATEGORY_KEYS.map((k) => (
              <option key={k} value={DAYWORK_CATEGORY_LABEL[k]}>{k} {DAYWORK_CATEGORY_LABEL[k]}</option>
            ))}
            {/* keep a value stored by an older build selectable */}
            {category && !CATEGORY_KEYS.some((k) => DAYWORK_CATEGORY_LABEL[k] === category) && <option value={category}>{category}</option>}
          </select>
        </div>
      )}

      {/* ── lines: what was built. NO RATES — 预算 prices them later (rates_not_allowed_here). ── */}
      <div className="field">
        <label>明细行 / lines — 只填内容与工程量，不填单价（单价由预算审核）/ scope and quantities only; 预算 sets the rates</label>
        <div className="cmp-wrap">
          <table className="cmp">
            <thead>
              <tr><th>序号</th><th>清单项 / BOQ line</th><th>名称 / label</th><th>规格</th><th>单位</th><th>数量</th><th>工程量计算式</th><th></th></tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td><BoqPicker workItems={workItems} value={l.work_item_id ?? ''} onPick={(w) => setLine(i, {
                    work_item_id: w?.id ?? null,
                    item_label: w ? w.item_label : l.item_label,
                    unit: w?.unit ?? l.unit,
                  })} /></td>
                  <td><input value={l.item_label} onChange={(e) => setLine(i, { item_label: e.target.value })} style={{ minHeight: 44, minWidth: 160 }} placeholder="自填项 / free text" /></td>
                  <td><input value={l.spec_md ?? ''} onChange={(e) => setLine(i, { spec_md: e.target.value })} style={{ minHeight: 44, minWidth: 110 }} /></td>
                  <td><input value={l.unit ?? ''} onChange={(e) => setLine(i, { unit: e.target.value })} style={{ minHeight: 44, width: 70 }} /></td>
                  <td><input type="number" step="0.001" value={l.qty ?? ''} onChange={(e) => setLine(i, { qty: e.target.value === '' ? null : Number(e.target.value) })} style={{ minHeight: 44, width: 90 }} /></td>
                  <td><input value={l.measurement_calc_md ?? ''} onChange={(e) => setLine(i, { measurement_calc_md: e.target.value })} style={{ minHeight: 44, minWidth: 180 }} placeholder="例：3.2×4.5−1.2×2.1" /></td>
                  <td><button className="small" style={{ minHeight: 44 }} disabled={lines.length <= 1} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button className="small" style={{ minHeight: 44, marginTop: 6 }} onClick={() => setLines((ls) => [...ls, blankLine()])}>＋ 增加一行 / Add a line</button>
      </div>

      {/* 预估金额: a 容错额度 ROUTING figure, not a price — and never a PM's field. */}
      {!isPm && (
        <div className="field">
          <label>预估金额 ¥ / rough estimate（仅用于容错额度判定，不是报价）</label>
          <input type="number" step="0.01" value={estimate} onChange={(e) => setEstimate(e.target.value)} style={{ minHeight: 44, width: 160 }} />
          <div className="small muted">
            超过容错额度时需商务/公司先批准；额度目前由公司决定，未设定前仅提示不拦截。/ above the tolerance this needs 商务 sign-off; the figure is a company decision and is advisory until it is set.
          </div>
        </div>
      )}

      <label className="row small" style={{ gap: 6, alignItems: 'center', minHeight: 44 }}>
        <input type="checkbox" checked={reworkRequired} onChange={(e) => setReworkRequired(e.target.checked)} />
        本条为返工指令，需完成后上传照片确认（合同 9.1.4）/ this is a rework order — a completion photo will be required
      </label>

      {err && <div className="small" style={{ color: 'var(--bad)', margin: '6px 0' }}>{err}</div>}
      <div className="row-wrap" style={{ gap: 8, marginTop: 6 }}>
        <button style={{ minHeight: 44 }} disabled={busy || !canSave} onClick={() => saveDraft().then((d) => d && toast('草稿已保存 / draft saved', { kind: 'success' }))}>
          💾 保存草稿 / Save draft
        </button>
        <button className="btn-primary" style={{ minHeight: 44 }} disabled={busy || !canSave} onClick={saveAndIssue}>
          📤 下发 / Issue
        </button>
        <span className="small muted">下发后进入「待签字（未生效）」，三方签字齐全才生效。/ issuing starts the 9.1.1 signature round; it is not an order until all three sign.</span>
      </div>
    </div>
  );
}

/** Search the package's 工程量清单 by 序号 or 名称; picking one carries its unit onto the line. */
function BoqPicker({ workItems, value, onPick }: { workItems: WorkItem[]; value: string; onPick: (w: WorkItem | null) => void }) {
  const [q, setQ] = useState('');
  const hits = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const pool = needle
      ? workItems.filter((w) => `${w.seq_label ?? ''} ${w.item_label}`.toLowerCase().includes(needle))
      : workItems;
    return pool.slice(0, 200);
  }, [q, workItems]);
  return (
    <div style={{ minWidth: 190 }}>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜索清单 / search 序号·名称" style={{ minHeight: 44, width: '100%' }} />
      <select value={value} onChange={(e) => onPick(workItems.find((w) => w.id === e.target.value) ?? null)} style={{ minHeight: 44, width: '100%', marginTop: 4 }}>
        <option value="">不引用清单 / free-text line</option>
        {hits.map((w) => <option key={w.id} value={w.id}>{w.seq_label ? `${w.seq_label} ` : ''}{w.item_label}{w.unit ? ` · ${w.unit}` : ''}</option>)}
      </select>
    </div>
  );
}
